import {useState, useEffect} from 'react';
import {motion, AnimatePresence} from 'framer-motion';

import {Image, useNavigate, useUrl} from '@shopify/hydrogen';

import {StandardInput} from './FormInputs.client';
import {QuizForm} from './QuizForm.client';
import PortableText from '../portableText/PortableText.client';

export const NextButton = ({className, ...props}: any) => {
  return (
    <button
      {...props}
      className={`mb-6 h-8 w-8 rounded-full bg-brand p-2 text-white ${className}`}
    >
      <svg
        id="Layer_1"
        data-name="Layer 1"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 26 22"
        className="fill-current"
      >
        <path
          d="M14.29.29a1,1,0,0,0-.08,1.32l.08.1L22.59,10H1a1,1,0,0,0-.12,2H22.59l-8.3,8.29a1,1,0,0,0-.08,1.32l.08.1a1,1,0,0,0,1.32.08l.1-.08,10-10,.09-.11.08-.12.05-.11,0-.1,0-.12v-.23l0-.12,0-.11,0-.11-.06-.1-.06-.09-.08-.1-10-10A1,1,0,0,0,14.29.29Z"
          transform="translate(0 0)"
          fill="white"
        />
      </svg>
    </button>
  );
};
export const NextDivButton = ({className, ...props}: any) => {
  return (
    <div
      {...props}
      className={`mb-6 h-8 w-8 rounded-full bg-brand p-2 text-white ${className}`}
    >
      <svg
        id="Layer_1"
        data-name="Layer 1"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 26 22"
        className="fill-current"
      >
        <path
          d="M14.29.29a1,1,0,0,0-.08,1.32l.08.1L22.59,10H1a1,1,0,0,0-.12,2H22.59l-8.3,8.29a1,1,0,0,0-.08,1.32l.08.1a1,1,0,0,0,1.32.08l.1-.08,10-10,.09-.11.08-.12.05-.11,0-.1,0-.12v-.23l0-.12,0-.11,0-.11-.06-.1-.06-.09-.08-.1-10-10A1,1,0,0,0,14.29.29Z"
          transform="translate(0 0)"
          fill="white"
        />
      </svg>
    </div>
  );
};

const initialFormState = {
  isSignedUp: false,
  subscriberEmail: null,
  quizActive: true,
};

export const DripSubscribeForm = ({
  introText,
  backgroundColor,
  successMessage,
  tag,
  quiz,
  onClose,
}: any) => {
  const [dripForm, setDripForm] = useState(initialFormState);
  const [loading, setLoading] = useState(false);

  const location = useUrl();
  const navigate = useNavigate();

  const host = import.meta.env.PUBLIC_FUNCTION_HOST;

  // Handle Submit
  const handleDripSubmit = async (event: any) => {
    event.preventDefault();

    setLoading(true);
    await fetch(`${host}/drip-sign-up`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({email: dripForm?.subscriberEmail, tag}),
    }).then((_: any) => {
      setDripForm((prevValues) => ({
        ...prevValues,
        isSignedUp: true,
      }));
    });
  };

  // Start Quiz Look
  const startQuiz = () => {
    setDripForm((prevValues) => ({
      ...prevValues,
      quizActive: true,
    }));
  };

  useEffect(() => {
    navigate(`${location.pathname}?form_step=signup`);
  }, []);

  return (
    <section style={{background: backgroundColor && backgroundColor.hex}}>
      <AnimatePresence mode="wait">
        {!dripForm?.isSignedUp ? (
          <motion.form
            className="grid h-screen grid-cols-14 grid-rows-3 overflow-y-hidden"
            onSubmit={(e: any) => handleDripSubmit(e)}
            initial={{opacity: 0, y: 10}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: 10}}
            key="subForm"
          >
            <Image
              alt="drip form"
              width="inherit"
              height="inherit"
              className="absolute top-1/2 col-start-1 col-end-3 hidden -translate-y-1/2 lg:block"
              data={{
                url: 'https://cdn.sanity.io/images/15oppjc2/production/b010474f97b17e021e4352be23db0ecdad373131-424x470.png',
              }}
            />
            <div className="col-start-2 col-end-14 row-start-2 row-end-2 text-center md:col-start-3 md:col-end-13 lg:col-start-4 lg:col-end-12">
              <PortableText blocks={introText} />
              <div className="mt-15 grid grid-cols-2">
                <StandardInput
                  backgroundColor={backgroundColor}
                  type="email"
                  placeholder="Enter your email address"
                  className="col-start-1 col-end-3  row-start-1 row-end-1"
                  required={true}
                  handleChange={(event: any) => {
                    event.persist();
                    setDripForm((prevValues) => ({
                      ...prevValues,
                      subscriberEmail: event.target.value,
                    }));
                  }}
                />
                {loading ? (
                  <div className="col-start-2 col-end-2 row-start-1 row-end-1 self-center justify-self-end text-brand">
                    <svg
                      className="-ml-1 mr-3 h-5 w-5 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </div>
                ) : (
                  <NextButton
                    className="col-start-2 col-end-2 row-start-1 row-end-1 self-center justify-self-end"
                    type="submit"
                  />
                )}
              </div>
            </div>
          </motion.form>
        ) : !dripForm.quizActive ? (
          <motion.div
            className="grid h-screen grid-cols-14"
            initial={{opacity: 0, y: 10}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: 10}}
            key="successText"
          >
            <div className="col-start-2 col-end-14 row-start-2 row-end-2 text-center md:col-start-3 md:col-end-13 lg:col-start-5 lg:col-end-11">
              <PortableText blocks={successMessage} />
              {quiz?.attachQuiz && (
                <button
                  className="shadow-lg rounded-full bg-brand py-2 px-12 text-lg font-semibold text-white"
                  onClick={startQuiz}
                >
                  {quiz?.linkText}
                </button>
              )}
            </div>
          </motion.div>
        ) : (
          <QuizForm
            {...quiz}
            {...dripForm}
            onClose={onClose}
            backgroundColor={backgroundColor}
          />
        )}
      </AnimatePresence>
    </section>
  );
};
